export const MEDICAL_RECORD_SESSION_POLL_INTERVAL_MS = 5000;

export const isMedicalRecordSessionPostProcessingPending = (
	status?: MedicalRecords.MedicalRecordSessionPosProcessStatus,
): boolean => status === 'QUEUED' || status === 'STARTED';

export const isMedicalRecordSessionPostProcessingTerminal = (
	status?: MedicalRecords.MedicalRecordSessionPosProcessStatus,
): boolean =>
	status === 'COMPLETED' || status === 'FAILED' || status === 'SKIPPED';

export const isMedicalRecordSessionMissingListSummary = (
	recordSession: MedicalRecords.MedicalRecordSession,
): boolean => {
	if (recordSession.summary?.trim()) return false;
	if (recordSession.evolution_notes?.trim()) return false;
	return true;
};

const MIN_SESSIONS_WITHOUT_SUMMARY_FOR_LIST_POLL = 3;

export const resolveOldestSessionMissingListSummary = (
	recordSessions: MedicalRecords.MedicalRecordSession[],
	currentPlatformSessionId?: string | null,
): MedicalRecords.MedicalRecordSession | null => {
	const withoutSummary = recordSessions.filter(
		(recordSession) =>
			recordSession.session_id !== currentPlatformSessionId &&
			isMedicalRecordSessionMissingListSummary(recordSession),
	);

	if (withoutSummary.length < MIN_SESSIONS_WITHOUT_SUMMARY_FOR_LIST_POLL) {
		return null;
	}

	return withoutSummary.reduce((oldest, current) =>
		current.session_number < oldest.session_number ? current : oldest,
	);
};

export const resolveMedicalRecordSessionPollInterval = (
	data: MedicalRecords.MedicalRecordSession | undefined,
): number | false => {
	if (
		!data ||
		!isMedicalRecordSessionPostProcessingPending(data.pos_process_status)
	) {
		return false;
	}

	return MEDICAL_RECORD_SESSION_POLL_INTERVAL_MS;
};

export const resolveMedicalRecordSessionUntilSummaryPollInterval = (
	data: MedicalRecords.MedicalRecordSession | undefined,
): number | false => {
	if (!data) return MEDICAL_RECORD_SESSION_POLL_INTERVAL_MS;
	if (data.pos_process_status === 'FAILED') return false;
	if (data.summary?.trim() || data.evolution_notes?.trim()) return false;

	return MEDICAL_RECORD_SESSION_POLL_INTERVAL_MS;
};

export const areMedicalRecordSessionsEqualForList = (
	left: MedicalRecords.MedicalRecordSession,
	right: MedicalRecords.MedicalRecordSession,
): boolean =>
	left.id === right.id &&
	left.session_id === right.session_id &&
	left.session_number === right.session_number &&
	left.summary === right.summary &&
	left.short_summary === right.short_summary &&
	left.summary_tag === right.summary_tag &&
	left.short_summary_tag === right.short_summary_tag &&
	left.evolution_notes === right.evolution_notes &&
	left.evolution_notes_tag === right.evolution_notes_tag &&
	left.pos_process_status === right.pos_process_status;

export const mergeMedicalRecordSessionIntoList = (
	sessions: MedicalRecords.MedicalRecordSession[],
	updatedSession: MedicalRecords.MedicalRecordSession,
): MedicalRecords.MedicalRecordSession[] => {
	const index = sessions.findIndex(
		(recordSession) => recordSession.id === updatedSession.id,
	);
	if (index === -1) return sessions;
	if (areMedicalRecordSessionsEqualForList(sessions[index], updatedSession)) {
		return sessions;
	}

	const next = [...sessions];
	next[index] = updatedSession;
	return next;
};
